import React from 'react';
import Link from 'next/link';
import { Col, Container, Row, Button } from 'react-bootstrap';
import {
  FaMagnifyingGlass,
  FaUserCheck,
  FaFileInvoice,
  FaShieldHalved,
  FaPlay,
  FaArrowRight,
  FaCarSide,
  FaGavel,
  FaTriangleExclamation,
  FaBolt,
} from 'react-icons/fa6';
import { FaCheckCircle } from 'react-icons/fa';
import Seo from '@/components/seo';
import { useRouter } from 'next/router';

export default function HowItWorksPage() {
  const router = useRouter();
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Como consultar histórico veicular por placa na AboutCar',
    description: 'Passo a passo simples para consultar o histórico completo de um carro, moto ou caminhão informando apenas a placa.',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Escolha a Consulta Ideal',
        text: 'Selecione a opção de consulta veicular que melhor atende suas necessidades, da verificação básica ao laudo completo.'
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Informe a Placa do Veículo',
        text: 'Digite a placa do veículo e seus dados de contato para envio seguro do laudo.'
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Pague e Receba em Segundos',
        text: 'Conclua o pagamento via PIX ou cartão e receba o relatório completo imediatamente na tela e no e-mail.'
      }
    ]
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Como Funciona',
        item: 'https://aboutcar.com.br/como-funciona'
      }
    ]
  };

  const steps = [
    {
      num: '01',
      title: 'Escolha a Consulta Ideal',
      description: 'Selecione o relatório perfeito para a sua necessidade — desde verificações básicas até laudos completos com histórico de leilão, sinistro, débitos e restrições.',
      icon: <FaMagnifyingGlass className="fs-3 text-primary" />,
      tag: 'Seleção do Laudo',
    },
    {
      num: '02',
      title: 'Informe a Placa do Veículo',
      description: 'Insira a placa do carro, moto ou caminhão e informe seus dados básicos de contato para garantirmos o envio seguro do seu relatório.',
      icon: <FaFileInvoice className="fs-3 text-info" />,
      tag: 'Identificação Rápida',
    },
    {
      num: '03',
      title: 'Pague e Receba em Segundos',
      description: 'Conclua o pagamento via PIX ou Cartão de Crédito com total segurança. Nosso sistema consulta as bases oficiais em tempo real e exibe seu laudo imediatamente na tela e no e-mail.',
      icon: <FaUserCheck className="fs-3 text-success" />,
      tag: 'Resultado Instantâneo',
    },
  ];

  const benefits = [
    {
      title: 'Histórico de Leilão & Sinistro',
      description: 'Descubra se o veículo já passou por leilões judiciais, de seguradoras ou possui indício de sinistro grave.',
      icon: <FaGavel className="fs-4 text-warning" />,
    },
    {
      title: 'Débitos, IPVA & Multas',
      description: 'Verifique a existência de pendências financeiras, débitos estaduais, alienações e restrições administrativas.',
      icon: <FaTriangleExclamation className="fs-4 text-danger" />,
    },
    {
      title: 'Aceitação em Seguradoras',
      description: 'Saiba com antecedência se o seminovo possui restrições que dificultem a contratação de seguro auto.',
      icon: <FaShieldHalved className="fs-4 text-primary" />,
    },
    {
      title: 'Dados 100% Oficiais e Atualizados',
      description: 'Informações puxadas diretamente de bases oficiais e parceiros estratégicos com atualização em tempo real.',
      icon: <FaBolt className="fs-4 text-success" />,
    },
  ];

  return (
    <>
      <Seo
        title="Como Funciona a Consulta de Histórico Veicular por Placa | AboutCar"
        description="Descubra como funciona a consulta de histórico veicular na AboutCar. Siga o passo a passo fácil para consultar placas de carros, motos e caminhões em segundos."
        keywords="como funciona consulta veicular, como consultar placa de carro, passo a passo consulta placa, histórico veicular online"
        schema={[howToSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-light-subtle border-bottom py-5">
        <Container className="py-2 py-lg-4">
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <span className="badge bg-primary text-white rounded-pill px-3 py-2 text-uppercase fw-semibold mb-3 fs-7 d-inline-flex align-items-center gap-2">
                <FaCarSide size={14} /> AboutCar • Simplicidade &amp; Segurança
              </span>
              <h1 className="display-4 fw-extrabold text-dark mb-3 lh-sm">
                Como Funciona a <span className="text-primary">Consulta Veicular</span>
              </h1>
              <p className="lead text-secondary mb-4 fs-5" style={{ maxWidth: '620px' }}>
                Simplicidade, rapidez e transparência. Siga apenas 3 passos simples para consultar o histórico completo de qualquer veículo pela placa.
              </p>

              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle d-flex align-items-center gap-3 flex-grow-1 flex-sm-grow-0">
                  <div className="bg-primary-subtle text-primary p-2 rounded-3">
                    <FaBolt className="fs-4" />
                  </div>
                  <div>
                    <span className="d-block text-muted small fw-semibold">Rapidez</span>
                    <strong className="text-dark small">Resultado Instantâneo</strong>
                  </div>
                </div>
                <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle d-flex align-items-center gap-3 flex-grow-1 flex-sm-grow-0">
                  <div className="bg-success-subtle text-success p-2 rounded-3">
                    <FaShieldHalved className="fs-4" />
                  </div>
                  <div>
                    <span className="d-block text-muted small fw-semibold">Segurança</span>
                    <strong className="text-dark small">Bases 100% Oficiais</strong>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={5} className="text-center">
              <img
                src="/assets/images/how-it-works.svg"
                className="img-fluid max-h-350 drop-shadow-sm"
                border="0"
                alt="Como Funciona a Consulta Veicular AboutCar"
                loading="lazy"
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Step by Step Cards Section */}
      <section className="py-5 bg-white">
        <Container className="py-2 py-lg-4">
          <Row className="justify-content-center text-center mb-5">
            <Col lg={8}>
              <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-1 fw-semibold small mb-2">
                PASSO A PASSO SIMPLES
              </span>
              <h2 className="display-6 fw-bold text-dark mb-3">
                3 Passos para Garantir uma Compra Segura
              </h2>
              <p className="text-secondary fs-6 mb-0 mx-auto" style={{ maxWidth: '600px' }}>
                Entenda como nosso processo foi desenhado para ser rápido, intuitivo e sem complicações.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {steps.map((step) => (
              <Col lg={4} md={6} key={step.num}>
                <div className="h-100 p-4 rounded-4 bg-white border border-light-subtle shadow-sm hover-elevation transition-all position-relative d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="p-3 bg-light rounded-3 d-inline-flex align-items-center justify-content-center">
                        {step.icon}
                      </div>
                      <span className="display-6 fw-extrabold text-primary-subtle font-monospace">
                        {step.num}
                      </span>
                    </div>

                    <span className="badge bg-light text-secondary rounded-pill px-2 py-1 small fw-medium mb-2">
                      {step.tag}
                    </span>

                    <h3 className="h5 fw-bold text-dark mb-3">{step.title}</h3>
                    <p className="text-secondary fs-6 lh-base mb-0">
                      {step.description}
                    </p>
                  </div>

                  <div className="pt-4 mt-4 border-top d-flex align-items-center text-primary fw-semibold small">
                    <span>Etapa {step.num} concluída em segundos</span>
                    <FaCheckCircle className="ms-auto text-success" />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* What You Check Section (Benefits Grid) */}
      <section className="py-5 bg-white">
        <Container className="py-2 py-lg-4">
          <Row className="justify-content-center text-center mb-5">
            <Col lg={8}>
              <span className="badge bg-success-subtle text-success rounded-pill px-3 py-1 fw-semibold small mb-2">
                O QUE VOCÊ CONSULTA
              </span>
              <h2 className="display-6 fw-bold text-dark mb-3">
                Informações Cruciais no Seu Laudo
              </h2>
              <p className="text-secondary fs-6 mb-0 mx-auto" style={{ maxWidth: '620px' }}>
                Saiba quais dados são verificados no histórico veicular para tomar a melhor decisão.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            {benefits.map((item, index) => (
              <Col md={6} key={index}>
                <div className="p-4 rounded-4 bg-white border border-light-subtle shadow-sm h-100 d-flex align-items-start gap-3">
                  <div className="p-3 bg-light rounded-3 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="h6 fw-bold text-dark mb-2">{item.title}</h4>
                    <p className="text-secondary small mb-0 lh-base">
                      {item.description}
                    </p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-light-subtle border-top">
        <Container className="py-2 py-lg-4">
          <div className="bg-primary text-white rounded-5 p-4 p-md-5 shadow-lg position-relative overflow-hidden">
            <Row className="justify-content-center text-center position-relative" style={{ zIndex: 2 }}>
              <Col lg={9} xl={8}>
                <span className="badge bg-white text-primary rounded-pill px-3 py-2 text-uppercase fw-bold mb-3 fs-7 shadow-sm d-inline-flex align-items-center gap-2">
                  <FaShieldHalved size={14} /> Proteja o Seu Dinheiro
                </span>

                <h2 className="display-6 fw-extrabold text-white mb-3">
                  Pronto para Fazer uma Compra Segura?
                </h2>

                <p className="text-white opacity-90 mb-4 fs-5 mx-auto lh-base" style={{ maxWidth: '620px' }}>
                  Descubra todo o histórico do veículo em segundos antes de fechar negócio. Evite surpresas com leilão, sinistro ou débitos ocultos.
                </p>

                <div className="d-flex flex-column align-items-center gap-3">
                  <Button
                    variant="light"
                    size="lg"
                    className="fw-bold px-5 py-3 rounded-pill text-primary shadow-lg border-0 d-inline-flex align-items-center gap-2 hover-elevation transition-all fs-6"
                    onClick={() => router.push('/consultas-e-pacotes')}
                  >
                    <span>Consultar Placa Agora</span>
                    <FaArrowRight size={16} />
                  </Button>

                  <div className="d-flex flex-wrap justify-content-center gap-3 mt-2 text-white small fw-medium">
                    <span className="d-inline-flex align-items-center gap-1">
                      <FaCheckCircle className="text-warning" size={14} /> Sem mensalidade
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                      <FaCheckCircle className="text-warning" size={14} /> Resultado na tela e no e-mail
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                      <FaCheckCircle className="text-warning" size={14} /> Pagamento seguro via PIX ou Cartão
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </>
  );
}

